/* eslint-disable react/function-component-definition */
import React from 'react';
import getConfig from 'next/config';
import Head from 'next/head';
import { Typography, Container, ContainerProps } from '@mui/material';
import classNames from 'classnames';
import { CommonMessages } from '@klokhuis/core/CommonMessages';
import { useIntl } from '@klokhuis/core/Intl';
import { Locale } from '@klokhuis/core/Intl/types';
import { AppRoute, AppRouteParams } from '@klokhuis/core/Routing';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { LoadingIndicator } from './components/LoadingIndicator';
import styles from './PageLayout.module.css';

const { publicRuntimeConfig } = getConfig();

export interface PageLayoutPagination {
  /** Current page number (1-based) */
  page: number;
  /** Total number of pages */
  totalPages: number;
  /** Canonical base URL of the paginated page (without ?page=N) */
  baseUrl: string;
}

export interface PageLayoutProps {
  pageTitle: string;
  metaDescription?: string;
  route?: AppRoute<any>;
  routeParams?: AppRouteParams;
  /** Route params for the Tamil (ta) locale alternate link */
  taRouteParams?: AppRouteParams;
  /** @deprecated Use taRouteParams instead */
  nlRouteParams?: AppRouteParams;
  loading?: boolean;
  children: React.ReactNode;
  className?: string;
  ogImage?: string;
  ogImageAlt?: string;
  hideFooter?: boolean;
  isBigHeader?: boolean;
  showPropertyLinksGrid?: boolean;
  /** Pagination info — emits rel=prev / rel=next links for multi-page result sets */
  pagination?: PageLayoutPagination;
  /** JSON-LD structured data (single object or array) */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export const PageLayout = ({
  pageTitle,
  metaDescription,
  route,
  routeParams,
  taRouteParams,
  nlRouteParams,
  loading = false,
  children,
  className,
  ogImage,
  ogImageAlt,
  hideFooter,
  isBigHeader = true,
  showPropertyLinksGrid = true,
  pagination,
  jsonLd,
}: PageLayoutProps) => {
  const { T, locale } = useIntl();

  // Support legacy nlRouteParams as fallback for taRouteParams
  const altRouteParams = taRouteParams ?? nlRouteParams;

  const title = React.useMemo(() => {
    return `${pageTitle.replace(
      // eslint-disable-next-line custom/no-restricted-translation-concatenation-with-template-literal
      ` | ${T(CommonMessages.app_name)}`,
      '',
      // eslint-disable-next-line custom/no-restricted-translation-concatenation-with-template-literal
    )} | ${T(CommonMessages.app_name)}`;
  }, [T, pageTitle]);

  const canonicalUrl = React.useMemo(() => {
    if (!route) return publicRuntimeConfig.ORIGIN as string;
    return locale === Locale.en
      ? publicRuntimeConfig.ORIGIN + route.url({ ...routeParams })
      : publicRuntimeConfig.ORIGIN + route.url({ ...altRouteParams });
  }, [route, locale, routeParams, altRouteParams]);

  const jsonLdArray = React.useMemo(() => {
    if (!jsonLd) return [];
    return Array.isArray(jsonLd) ? jsonLd : [jsonLd];
  }, [jsonLd]);

  return (
    <React.Fragment>
      <Head>
        <title>{title}</title>
        {metaDescription && (
          <meta name="description" content={metaDescription} />
        )}

        {/* ── Open Graph ── */}
        <meta property="og:site_name" content={T(CommonMessages.app_name)} />
        <meta property="og:title" content={title} />
        {metaDescription && (
          <meta property="og:description" content={metaDescription} />
        )}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta
          property="og:locale"
          content={locale === Locale.ta ? 'ta_IN' : 'en_IN'}
        />
        {ogImage && <meta property="og:image" content={ogImage} />}
        {ogImage && <meta property="og:image:width" content="1200" />}
        {ogImage && <meta property="og:image:height" content="630" />}
        {ogImage && (
          <meta property="og:image:alt" content={ogImageAlt ?? title} />
        )}

        {/* ── Twitter Card ── */}
        <meta
          name="twitter:card"
          content={ogImage ? 'summary_large_image' : 'summary'}
        />
        <meta name="twitter:title" content={title} />
        {metaDescription && (
          <meta name="twitter:description" content={metaDescription} />
        )}
        {ogImage && <meta name="twitter:image" content={ogImage} />}
        {ogImage && (
          <meta name="twitter:image:alt" content={ogImageAlt ?? title} />
        )}

        {/* ── Canonical + hreflang ── */}
        {route && (
          <React.Fragment>
            <link rel="canonical" href={canonicalUrl} />
            <link
              rel="alternate"
              hrefLang="x-default"
              href={publicRuntimeConfig.ORIGIN + route.url({ ...routeParams })}
            />
            <link
              rel="alternate"
              hrefLang={Locale.en}
              href={publicRuntimeConfig.ORIGIN + route.url({ ...routeParams })}
            />
            <link
              rel="alternate"
              hrefLang={Locale.ta}
              href={
                publicRuntimeConfig.ORIGIN + route.url({ ...altRouteParams })
              }
            />
          </React.Fragment>
        )}

        {/* ── Pagination rel=prev / rel=next ── */}
        {pagination && pagination.page > 1 && (
          <link
            rel="prev"
            href={`${pagination.baseUrl}${
              pagination.page === 2 ? '' : `?page=${pagination.page - 1}`
            }`}
          />
        )}
        {pagination && pagination.page < pagination.totalPages && (
          <link
            rel="next"
            href={`${pagination.baseUrl}?page=${pagination.page + 1}`}
          />
        )}

        {/* ── JSON-LD Structured Data ── */}
        {jsonLdArray.map((schema, i) => (
          <script
            key={`jsonld-${i}`}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
          />
        ))}
      </Head>
      <div className={classNames(styles.root, className)}>
        <Header isBigHeader={isBigHeader} />

        <LoadingIndicator loading={loading} />

        {children}

        {!hideFooter && (
          <Footer showPropertyLinksGrid={showPropertyLinksGrid} />
        )}
      </div>
    </React.Fragment>
  );
};

PageLayout.MainContainer = function PageLayoutMainContainer({
  children,
  ...props
}: Pick<React.HTMLAttributes<HTMLDivElement>, 'children' | 'className'> &
  Pick<ContainerProps, 'maxWidth'>) {
  return (
    <Container
      id="page-content"
      data-testid="PageContent"
      component="main"
      {...props}
    >
      {children}
    </Container>
  );
};

PageLayout.MainRaw = function PageLayoutMainRaw({
  children,
  className,
}: Pick<React.HTMLAttributes<HTMLDivElement>, 'children' | 'className'>) {
  return (
    <main id="page-content" data-testid="PageContent" className={className}>
      {children}
    </main>
  );
};

PageLayout.SectionRaw = function PageLayoutSectionRaw({
  id,
  'data-testid': testid,
  className,
  children,
}: Pick<
  React.HTMLAttributes<HTMLDivElement>,
  'id' | 'className' | 'children'
> & {
  'data-testid'?: string;
}) {
  return (
    <section id={id} data-testid={testid} className={className}>
      {children}
    </section>
  );
};

PageLayout.SectionContainer = function PageLayoutSectionContainer({
  id,
  'data-testid': testid,
  className,
  containerClassName,
  children,
}: Pick<
  React.HTMLAttributes<HTMLDivElement>,
  'id' | 'className' | 'children'
> & {
  'data-testid'?: string;
  containerClassName?: React.HTMLAttributes<HTMLDivElement>['className'];
}) {
  return (
    <section id={id} data-testid={testid} className={className}>
      <Container className={containerClassName}>{children}</Container>
    </section>
  );
};

PageLayout.Title = function PageLayoutTitle({
  children,
  className,
}: Pick<React.HTMLAttributes<HTMLDivElement>, 'children' | 'className'>) {
  return (
    <Typography
      variant="h2"
      data-testid="PageTitle"
      className={classNames(styles.pageTitle, className)}
    >
      {children}
    </Typography>
  );
};
